import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Folder,
  FolderOpen,
  BookOpen,
  Database,
  Wrench,
  GitBranch,
  PhoneTransfer,
  Code,
  NotePencil,
  Hash,
  Chat,
  Envelope,
  Truck,
  Gear,
  Globe,
  FlowArrow,
  Users,
  PencilSimple,
  WebhooksLogo,
  ChatCircle,
  Eye,
  EyeSlash,
  Check,
  WarningCircle,
  CircleNotch,
  Buildings,
  Shield,
  Plug,
  Lightning,
  Power,
  Trash,
  AndroidLogo,
  ShieldWarning,
  Timer,  // Add Timer icon for Post Call Actions
  Clock   // Add Clock icon for Activation Conditions
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { getSupabaseClient } from '../supabaseClient';
import { useBusinessType } from '../context/BusinessContext';
import { runAutomaticConflictDetection } from '../utils/conflictDetection';
import { cn } from '../lib/utils';
import { CurbsideIcon } from '../utils/customIcons';
import CurbsideIntegration from '../integrations/curbsideIntegration';
import { API_BASE_URL, endpoints } from '../config/api';

// Import the existing modal components - we'll render them inline
import CallActionSettingsModal from './call-actions/CallActionSettingsModal';
import { SimpleActionContentModal } from './call-actions/SimpleActionContentModal';
import { ComplexAnswerSettingsModal } from './complex-answer/ComplexAnswerSettingsModal';
import { ComplexAnswerContentModal } from './complex-answer/ComplexAnswerContentModal';
import SimpleAnswerModal from '../brain/SimpleAnswerModal';
import WorkflowModal from './workflow/WorkflowModal';
import ConnectedIntegrationView from './ConnectedIntegrationView';
import MultilingualSettings from './MultilingualSettings';
import PostCallActionModal from './PostCallActionModal';
import ClarificationQuestionEditor from './brain/ClarificationQuestionEditor';
import FirstMessageEditor from './FirstMessageEditor';
import IntegrationV2Content from './integrations/IntegrationV2Content';

const BrainMainContent = ({ 
  selectedItem, 
  categories = [], 
  integrations = [],
  clarificationQuestions = [],
  onEdit, 
  onDelete, 
  onAddLocationVariant,
  onUpdateSetting,
  onAddSetting,
  onUpdateIntegration,
  onUpdateMultilingualSettings,
  onUpdateClarificationQuestion,
  onDeleteClarificationQuestion,
  onOpenIntegrationDetails,
  onCoverageRefresh,
  onMobileSidebarToggle,
  onCreateCategory,
  newlyCreatedCategory,
  onClearNewlyCreatedCategory,
  isMobile = false,
  isSidePanelOpen = false,
  onOpenActivationConditions,
  // V2 Integration props
  integrationsV2 = [],
  isLoadingV2 = false,
  onConnectIntegrationV2,
  onUpdateIntegrationCredentialsV2,
  onDisconnectIntegrationV2,
  onTriggerSyncV2,
  onToggleIntegrationStatusV2
}) => {
  const { businessType } = useBusinessType();
  const supabase = getSupabaseClient(businessType);
  const navigate = useNavigate();

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false); // NEW: State for LocationModal
  const [currentSetting, setCurrentSetting] = useState(null);
  const [syncedSelectedItem, setSyncedSelectedItem] = useState(selectedItem);
  const [curbsideData, setCurbsideData] = useState(null);

  // Integration-specific state
  const [integrationProvider, setIntegrationProvider] = useState(null);
  const [integrationAuthConfig, setIntegrationAuthConfig] = useState(null);
  const [existingIntegration, setExistingIntegration] = useState(null);
  const [integrationLoading, setIntegrationLoading] = useState(false);
  const [integrationConnecting, setIntegrationConnecting] = useState(false);
  const [integrationError, setIntegrationError] = useState(null);
  const [integrationSuccess, setIntegrationSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [integrationCapabilities, setIntegrationCapabilities] = useState({
    simpleAnswers: { count: 0, items: [] },
    complexAnswers: { count: 0, items: [] },
    simpleActions: { count: 0, items: [] },
    workflows: { count: 0, items: [] }
  });

  // Tab state for Curbside integration
  const [curbsideActiveTab, setCurbsideActiveTab] = useState('ai-works');

  // Integration form data state
  const [integrationFormData, setIntegrationFormData] = useState({});

  // Keep selectedItem in sync with updated data from categories and integrations
  useEffect(() => {
    if (!selectedItem || (!categories.length && !integrations.length)) {
      setSyncedSelectedItem(selectedItem);
      return;
    }

    if (selectedItem.type === 'multilingual') {
      setSyncedSelectedItem(selectedItem);
      return;
    }
    if (selectedItem.type === 'firstMessage') {
      setSyncedSelectedItem(selectedItem);
      return;
    }

    // For integrations, find the updated version
    if (selectedItem.type === 'integration') {
      const updatedIntegration = integrations.find(integration => integration.id === selectedItem.id);
      // Preserve the 'type' property from selectedItem while updating with fresh data
      setSyncedSelectedItem({ ...selectedItem, ...updatedIntegration });
      return;
    }

    // For categories, find the updated version
    if (selectedItem.type === 'category') {
      const updatedCategory = categories.find(cat => cat.id === selectedItem.id);
      setSyncedSelectedItem(updatedCategory || selectedItem);
      return;
    }

    // For settings, find the updated version within its category
    if (selectedItem.type === 'setting') {
      for (const category of categories) {
        if (category.settings) {
          const updatedSetting = category.settings.find(setting => setting.id === selectedItem.id);
          if (updatedSetting) {
            setSyncedSelectedItem({
              ...updatedSetting,
              type: 'setting',
              category_name: category.name
            });
            return;
          }
        }
      }
    }

    // For variants, find the updated version
    if (selectedItem.type === 'variant') {
      for (const category of categories) {
        if (category.settings) {
          for (const setting of category.settings) {
            if (setting.locationVariants) {
              const updatedVariant = setting.locationVariants.find(variant => variant.id === selectedItem.id);
              if (updatedVariant) {
                setSyncedSelectedItem({
                  ...updatedVariant,
                  type: 'variant',
                  parentSetting: setting,
                  category_name: category.name
                });
                return;
              }
            }
          }
        }
      }
    }

    // Fallback to original selectedItem if no updated version found
    setSyncedSelectedItem(selectedItem);
  }, [selectedItem, categories, integrations]);

  // Handle selectedItem changes - close modal if no item, or re-initialize edit mode if item changed
  useEffect(() => {
    // If no item is selected, close the edit modal
    if (!syncedSelectedItem) {
      setIsEditModalOpen(false);
      setCurrentSetting(null);
      return;
    }

    // If an item is selected and it's a setting or variant, ensure we're in edit mode with the correct data
    if (syncedSelectedItem.type === 'setting' || syncedSelectedItem.type === 'variant') {
      // Only re-initialize edit mode if we're not already editing this same item
      // This prevents re-initialization when just updating data after a save
      const isAlreadyEditingThisItem = isEditModalOpen && currentSetting && 
        ((syncedSelectedItem.type === 'variant' && currentSetting.grouping_id === syncedSelectedItem.id) ||
         (syncedSelectedItem.type === 'setting' && currentSetting.settings_id === syncedSelectedItem.id));

      if (!isAlreadyEditingThisItem) {
        handleEdit(syncedSelectedItem);
      }
    }
  }, [syncedSelectedItem, isEditModalOpen, currentSetting]);

  // Fetch Curbside data for integration display
  const fetchCurbsideData = async () => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) return;

      const { data: settings } = await supabase
        .from('curbside_settings')
        .select('service_types, service_options')
        .eq('account_id', accountId)
        .maybeSingle();

      const { data: routes } = await supabase
        .from('curbside_route_settings')
        .select('route_name, wash_and_fold_turnaround, dry_cleaning_turnaround, days, times')
        .eq('account_id', accountId)
        .limit(10); // Fetch more to show first 3 plus count

      const { data: zones } = await supabase
        .from('curbside_zone_settings')
        .select('zone_name, price_per_pound_laundry, order_minimum, price_add_per_item, price_add_dry_cleaning, service_areas')
        .eq('account_id', accountId)
        .limit(10); // Fetch more to show first 3 plus count

      setCurbsideData({
        serviceTypes: settings?.service_types || 'laundry',
        serviceOptions: settings?.service_options || [],
        routes: routes || [],
        zones: zones || []
      });
    } catch (err) {
      console.error('Error fetching Curbside data:', err);
    }
  };

  useEffect(() => {
    // Only fetch if this is a Curbside integration
    if (syncedSelectedItem?.type === 'integration' && syncedSelectedItem?.id === 'curbside-integration') {
      fetchCurbsideData();
    }

    // V2 integrations handle their own data fetching in IntegrationV2Content component
    // No need to fetch integration data here for V2 integrations
  }, [syncedSelectedItem]);

  // Fetch integration data for non-Curbside integrations
  const fetchIntegrationData = async () => {
    try {
      setIntegrationLoading(true);
      setIntegrationError(null);

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      // Fetch provider details
      const { data: providerData, error: providerError } = await supabase
        .from('api_providers')
        .select('*')
        .eq('slug', syncedSelectedItem.slug)
        .eq('is_active', true)
        .single();

      if (providerError) throw providerError;
      setIntegrationProvider(providerData);

      // Fetch auth configuration
      const { data: authConfigData, error: authConfigError } = await supabase
        .from('auth_provider_configs')
        .select(`
          *,
          auth_methods (
            id,
            name,
            requires_refresh
          ),
          auth_provider_config_fields (
            field_name,
            label,
            field_type,
            placeholder,
            is_required,
            display_order
          )
        `)
        .eq('provider_id', providerData.id)
        .order('display_order', { foreignTable: 'auth_provider_config_fields', ascending: true })
        .single();

      if (authConfigError) throw authConfigError;
      setIntegrationAuthConfig(authConfigData);

      // Check for existing integration
      const { data: integrationData, error: integrationError } = await supabase
        .from('accounts_integrations')
        .select('*')
        .eq('account_id', accountId)
        .eq('provider_id', providerData.id)
        .eq('is_active', true)
        .maybeSingle();

      if (integrationError && integrationError.code !== 'PGRST116') throw integrationError;

      if (integrationData) {
        setExistingIntegration(integrationData);

        const loadedFormData = {};

        // Load metadata
        if (integrationData.metadata && authConfigData.auth_provider_config_fields) {
          authConfigData.auth_provider_config_fields.forEach(field => {
            if (integrationData.metadata[field.field_name]) {
              loadedFormData[field.field_name] = integrationData.metadata[field.field_name];
            }
          });
        }

        // Load existing credentials (show as masked for security)
        const { data: credentialsData, error: credentialsError } = await supabase
          .from('api_credentials')
          .select('credential_type')
          .eq('integration_id', integrationData.id);

        if (!credentialsError && credentialsData && authConfigData.auth_provider_config_fields) {
          authConfigData.auth_provider_config_fields.forEach(field => {
            if (credentialsData.some(c => c.credential_type === field.field_name)) {
              loadedFormData[field.field_name] = '••••••••';
            }
          });
        }

        setIntegrationFormData(loadedFormData);

      } else {
        // Reset form for new integration
        setIntegrationFormData({});
      }

    } catch (err) {
      console.error('Error fetching integration data:', err);
      setIntegrationError('Failed to load integration details. Please try again.');
    } finally {
      setIntegrationLoading(false);
    }
  };

  // Calculate capabilities when integration provider changes
  useEffect(() => {
    const calculateCapabilities = async () => {
      if (integrationProvider?.id) {
        try {
          const capabilities = await calculateIntegrationCapabilities(integrationProvider.id);
          setIntegrationCapabilities(capabilities);
        } catch (err) {
          console.error('Error calculating capabilities:', err);
          setIntegrationCapabilities({
            simpleAnswers: { count: 0, items: [] },
            complexAnswers: { count: 0, items: [] },
            simpleActions: { count: 0, items: [] },
            workflows: { count: 0, items: [] }
          });
        }
      }
    };

    calculateCapabilities();
  }, [integrationProvider?.id, categories]);

  // Handle integration form input changes
  const handleIntegrationInputChange = (field, value) => {
    setIntegrationFormData(prev => ({ ...prev, [field]: value }));
    setIntegrationError(null);
    setIntegrationSuccess(false);
  };

  // Connect integration (test credentials and save)
  const connectIntegration = async () => {
    try {
      setIntegrationConnecting(true);
      setIntegrationError(null);

      // Dynamic validation based on fetched fields
      if (integrationAuthConfig && integrationAuthConfig.auth_provider_config_fields) {
        for (const field of integrationAuthConfig.auth_provider_config_fields) {
          if (field.is_required && !integrationFormData[field.field_name]) {
            throw new Error(`${field.label} is required.`);
          }
          // Don't allow updating with masked passwords
          if (existingIntegration && integrationFormData[field.field_name] === '••••••••') {
             throw new Error(`Please enter your actual ${field.label} to update the connection`);
          }
        }
      }

      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      // Get auth method name
      const authMethod = integrationAuthConfig.auth_methods.name;

      // Determine if this auth method needs backend processing
      const authTypesNeedingEndpoint = ['bearer_token', 'oauth2', 'jwt_bearer'];
      const needsBackendProcessing = authTypesNeedingEndpoint.includes(authMethod);

      let savedIntegration = null;

      if (needsBackendProcessing) {
        // For complex auth types, use the backend endpoint
      const response = await fetch(`${API_BASE_URL}${endpoints.integrationsConnections}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          account_id: accountId,
          provider_slug: integrationProvider.slug,
            auth_method: authMethod,
            credentials: { ...integrationFormData },
          name: `${integrationProvider.name} Integration`,
          description: `Integration for ${integrationProvider.name}`
        })
      });

      const responseData = await response.json();

      if (!response.ok) {
        throw new Error(responseData.error || 'Connection failed. Please check your credentials.');
      }

        savedIntegration = responseData.connection;
      } else {
        // For simple auth types (no_auth, basic_auth, api_key), save directly

        // First, get the auth method ID
        const { data: authMethodData, error: authMethodError } = await supabase
          .from('auth_methods')
          .select('id')
          .eq('name', authMethod)
          .single();

        if (authMethodError) throw authMethodError;

        // Create or update the integration record
        const integrationData = {
          account_id: accountId,
          provider_id: integrationProvider.id,
          auth_method_id: authMethodData.id,
          is_active: true,
          integration_status: 'complete',
          metadata: {
            name: `${integrationProvider.name} Integration`,
            description: `Integration for ${integrationProvider.name}`
          }
        };

        // Use upsert to handle both create and update cases
        const { data: upsertedIntegration, error: upsertError } = await supabase
          .from('accounts_integrations')
          .upsert(integrationData, {
            onConflict: 'account_id,provider_id',
            ignoreDuplicates: false
          })
          .select()
          .single();

        if (upsertError) throw upsertError;
        savedIntegration = upsertedIntegration;

        // Delete existing credentials first (if any)
        await supabase
          .from('api_credentials')
          .delete()
          .eq('integration_id', savedIntegration.id);

        // Save credentials for auth types that need them
        if (authMethod !== 'no_auth' && Object.keys(integrationFormData).length > 0) {
          const credentialInserts = Object.entries(integrationFormData).map(([key, value]) => ({
            integration_id: savedIntegration.id,
            credential_type: key,
            encrypted_value: value // Note: In production, this should be encrypted
          }));

          const { error: credentialsError } = await supabase
            .from('api_credentials')
            .insert(credentialInserts);

          if (credentialsError) throw credentialsError;
        }
      }

      setExistingIntegration(savedIntegration);
      setIntegrationSuccess('Connected successfully!');

      // Update the selected item to show as connected
      setSyncedSelectedItem(prev => ({
        ...prev,
        status: 'complete',
        is_active: true
      }));

      toast.success(`${integrationProvider.name} connected successfully!`);

    } catch (err) {
      console.error('Error connecting integration:', err);
      setIntegrationError(err.message || 'Failed to connect. Please check your credentials and try again.');
    } finally {
      setIntegrationConnecting(false);
    }
  };

  // Disconnect integration
  const disconnectIntegration = async () => {
    if (!confirm('Are you sure you want to disconnect this integration?')) return;

    try {
      setIntegrationConnecting(true);
      setIntegrationError(null);

      // Deactivate the integration
      const { error: updateError } = await supabase
        .from('accounts_integrations')
        .update({ 
          is_active: false,
          integration_status: 'not_started'
        })
        .eq('id', existingIntegration.id);

      if (updateError) throw updateError;

      setIntegrationSuccess('Disconnected successfully!');
      setExistingIntegration(null);

      // Update the selected item to show as unconnected
      setSyncedSelectedItem(prev => ({
        ...prev,
        status: 'unconnected',
        is_active: false
      }));

      // Reset form
      setIntegrationFormData({});

      toast.success(`${integrationProvider.name} disconnected successfully!`);

    } catch (err) {
      console.error('Error disconnecting integration:', err);
      setIntegrationError(err.message || 'Failed to disconnect integration. Please try again.');
    } finally {
      setIntegrationConnecting(false);
    }
  };

  // Toggle integration active status
  const toggleIntegrationStatus = async () => {
    if (!existingIntegration && !syncedSelectedItem) return;

    try {
      setIntegrationConnecting(true);
      setIntegrationError(null);

      const currentStatus = existingIntegration?.is_active ?? syncedSelectedItem?.is_active ?? false;
      const newStatus = !currentStatus;

      // Update local state immediately for instant UI feedback
      if (onUpdateIntegration) {
        onUpdateIntegration(syncedSelectedItem.id, { 
          is_active: newStatus,
          status: 'complete' // Keep connected status, just toggle active
        });
      }

      // Update database - find integration by provider if existingIntegration doesn't exist
      let integrationToUpdate = existingIntegration;

      if (!integrationToUpdate && syncedSelectedItem?.integration_id) {
        // Try to get the integration record by the actual integration ID
        const { data: foundIntegration, error: findError } = await supabase
          .from('accounts_integrations')
          .select('*')
          .eq('id', syncedSelectedItem.integration_id)
          .single();

        if (!findError && foundIntegration) {
          integrationToUpdate = foundIntegration;
          setExistingIntegration(foundIntegration);
        }
      }

      if (!integrationToUpdate) {
        throw new Error('Integration record not found');
      }

      const { error: updateError } = await supabase
        .from('accounts_integrations')
        .update({ is_active: newStatus })
        .eq('id', integrationToUpdate.id);

      if (updateError) throw updateError;

      setExistingIntegration(prev => ({ ...prev, is_active: newStatus }));

      // Update the selected item to reflect the new status
      setSyncedSelectedItem(prev => ({
        ...prev,
        status: 'complete', // Keep connected, just toggle active
        is_active: newStatus
      }));

      toast.success(`Integration ${newStatus ? 'activated' : 'deactivated'} successfully`);

    } catch (err) {
      console.error('Error toggling integration status:', err);
      setIntegrationError('Failed to update integration status');

      // Revert local state on error
      if (onUpdateIntegration) {
        onUpdateIntegration(syncedSelectedItem.id, { 
          is_active: currentStatus, // Revert to the original status
          status: 'complete'
        });
      }
    } finally {
      setIntegrationConnecting(false);
    }
  };

  // Delete integration
  const deleteIntegration = async () => {
    if (!existingIntegration) return;

    // Show confirmation dialog
    if (!window.confirm('Are you sure you want to delete this integration? This action cannot be undone.')) {
      return;
    }

    try {
      setIntegrationConnecting(true);
      setIntegrationError(null);

      // Update local state immediately for instant UI feedback
      if (onUpdateIntegration) {
        onUpdateIntegration(syncedSelectedItem.id, { 
          status: 'unconnected',
          is_active: false
        });
      }

      // Handle Curbside special case
      if (syncedSelectedItem.slug === 'curbside') {
        const { getUserAccountId } = await import('../utils/accountUtils');
        const { accountId } = await getUserAccountId();

        const { error: deleteError } = await supabase
          .from('curbside_settings')
          .delete()
          .eq('account_id', accountId);

        if (deleteError) throw deleteError;
      } else {
        // Delete from accounts_integrations (CASCADE will handle related records)
        const { error: deleteError } = await supabase
          .from('accounts_integrations')
          .delete()
          .eq('id', existingIntegration.id);

        if (deleteError) throw deleteError;
      }

      setExistingIntegration(null);

      // Update the selected item to show as unconnected
      setSyncedSelectedItem(prev => ({
        ...prev,
        status: 'unconnected',
        is_active: false
      }));

      toast.success('Integration deleted successfully');

    } catch (err) {
      console.error('Error deleting integration:', err);
      setIntegrationError('Failed to delete integration');

      // Revert local state on error
      if (onUpdateIntegration) {
        onUpdateIntegration(syncedSelectedItem.id, { 
          status: 'connected',
          is_active: existingIntegration.is_active
        });
      }
    } finally {
      setIntegrationConnecting(false);
    }
  };

  // Render integration form fields
  const renderIntegrationAuthFields = () => {
    if (!integrationAuthConfig?.auth_provider_config_fields?.length) {
      return (
        <div className="text-center text-gray-500 py-4">
          This integration requires no specific configuration.
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {integrationAuthConfig.auth_provider_config_fields.map(field => (
          <div key={field.field_name}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {field.label}
            </label>
            <div className="relative">
              <input
                type={(field.field_type === 'password' && !showPassword) ? 'password' : 'text'}
                value={integrationFormData[field.field_name] || ''}
                onChange={(e) => handleIntegrationInputChange(field.field_name, e.target.value)}
                className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
                placeholder={field.placeholder || ''}
                required={field.is_required}
              />
              {field.field_type === 'password' && (
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  {showPassword ? (
                    <EyeSlash className="h-4 w-4 text-gray-400" />
                  ) : (
                    <Eye className="h-4 w-4 text-gray-400" />
                  )}
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getIcon = (item) => {
    if (!item) return Folder;

    // For integrations, use the icon from the provider
    if (item.type === 'integration') {
      if (item.id === 'curbside-integration' || item.slug === 'curbside' || item.integration_type === 'curbside') {
          return CurbsideIcon;
      }
      return Buildings;
    }

    // Handle Post Call Actions
    if (item.type === 'postCallAction') {
      return Timer;
    }

    const icons = {
      simple_answer: BookOpen,
      complex_answer: Database,
      simple_action: Wrench,
      workflow: GitBranch,
      transfer: PhoneTransfer,
      api: Code,
      dtmf: NotePencil,
      sms: Chat,
      email: Envelope,
      webhook: WebhooksLogo,
      note: NotePencil,
      // Specific system categories
      'delivery-driver': Truck,
      'driver': Truck,
      'delivery': Truck,
      'troubleshooting': Gear,
      'trouble': Gear,
      'global-settings': Globe,
      'global': Globe,
      'control-flow': FlowArrow,
      'control': FlowArrow,
      'flow': FlowArrow,
      'hr': Users,
      'human-resources': Users,
      'human': Users,
      'resources': Users,
      // Default
      'default': Folder
    };

    // First try exact match on type/original_type
    const type = item.original_type || item.type || item.category_type;
    if (icons[type]) return icons[type];

    // Then try name-based matching
    const lowercaseName = (item.name || '').toLowerCase();
    const matchedKey = Object.keys(icons).find(key => lowercaseName.includes(key));

    return icons[matchedKey] || icons.default;
  };

  const getTypeLabel = (item) => {
    if (!item) return 'Unknown';

    if (item.type === 'category') {
      return 'Category';
    }

    if (item.type === 'integration') {
      return 'Integration';
    }

    if (item.type === 'postCallAction') {
      return 'Post Call Action';
    }

    // For variants, use the parent setting type
    if (item.type === 'variant' && item.parentSetting) {
      const itemType = item.parentSetting.original_type || item.parentSetting.type;
      switch (itemType) {
        case 'simple_answer':
          return 'Simple Answer Variant';
        case 'complex_answer':
          return 'Complex Answer Variant';
        case 'simple_action':
          return 'Simple Action Variant';
        case 'workflow':
          return 'Workflow Variant';
        default:
          return 'Variant';
      }
    }

    const itemType = item.type === 'setting' ? item.original_type : item.type;

    switch (itemType) {
      case 'simple_answer':
        return 'Simple Answer';
      case 'complex_answer':
        return 'Complex Answer';
      case 'simple_action':
        return 'Simple Action';
      case 'workflow':
        return 'Workflow';
      default:
        return 'Setting';
    }
  };

  const handleEdit = async (item) => {
    setIsEditModalOpen(true);

    try {
      // Handle new items (with temporary IDs) - no database loading needed
      if (item.isNew || (item.id && item.id.toString().startsWith('new-'))) {
        setCurrentSetting({
          ...item,
          settings_id: item.id,
          isPreLoaded: false,
          isNew: true
        });
        return;
      }

      // Handle variants - both with and without pre-loaded data
      if (item.type === 'variant') {
        if (item.fullData) {
          setCurrentSetting({ 
            ...item.fullData, 
            isPreLoaded: true,
            grouping_id: item.id, // Ensure we have the grouping_id for comparison
            variant_id: item.id // Alternative field name for safety
          });
        } else {
          // For variants without fullData (blank variants), use the variant itself
          setCurrentSetting({ 
            ...item, 
            isPreLoaded: false,
            grouping_id: item.id, // Ensure we have the grouping_id for comparison
            variant_id: item.id // Alternative field name for safety
          });
        }
        return;
      }

      // Handle regular settings (existing items from database)
      if (item.type === 'setting') {
        if (item.original_type === 'simple_action') {
          const action = await loadSimpleAction(item);
          setCurrentSetting({ 
            ...action, 
            settings_id: item.id // Ensure we have the settings_id for comparison
          });
        } else if (item.original_type === 'simple_answer') {
          const answer = await loadSimpleAnswer(item);
          setCurrentSetting({ 
            ...answer, 
            settings_id: item.id // Ensure we have the settings_id for comparison
          });
        } else {
          // For complex answers and other types, use the setting as-is for now
          // The modal itself will fetch more details if needed
          setCurrentSetting({ 
            ...item, 
            settings_id: item.id // Ensure we have the settings_id for comparison
          });
        }
      }
    } catch (err) {
      console.error('Error loading setting details for inline editing:', err);
      toast.error('Failed to load setting details');
      setIsEditModalOpen(false);
    }
  };

  const handleCloseEdit = (saveResult) => {
    // This function now elegantly handles success cases for both inline and pop-up modals first.
    if (saveResult && saveResult.success) {
      // 1. If a new item was created, add it to the local UI state immediately.
      if (saveResult.isCreate && saveResult.data && onAddSetting) {
        onAddSetting(saveResult.data);
      }

      // 2. Always run background tasks on success.
      runAutomaticConflictDetection(supabase);
      if (onCoverageRefresh && saveResult.settingsId) {
        // This triggers a refresh of the AI Coverage percentage.
        // Get account ID and call the coverage analysis API
        import('../utils/accountUtils').then(({ getUserAccountId }) => {
          getUserAccountId().then(({ accountId }) => {
            if (accountId) {
              // Call the coverage analysis API directly for this specific setting
              fetch(`${API_BASE_URL}${endpoints.coverageSetting}`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  account_id: accountId,
                  settings_id: saveResult.settingsId
                })
              }).then(response => {
                if (response.ok) {
                  response.json().then(result => {
                    if (result.success) {
                      // Refresh coverage display for all users
                      if (onCoverageRefresh) {
                        onCoverageRefresh();
                      }
                    }
                  });
                }
              }).catch(error => {
                console.error('[BrainMainContent] Error analyzing setting coverage:', error);
              });
            }
          });
        });
      }

      // 3. For inline editors, we stop here to keep the view open for further editing.
      if (saveResult.shouldClose === false) {
        return;
      }
    }

    // This part of the function now only handles the closing of pop-up modals,
    // or when a user explicitly cancels an action.
    setIsLocationModalOpen(false);
    // Note: The inline editing view is controlled by `selectedItem` in Brain.js,
    // so no state change is needed here to "close" it.
  };

  const handleSaveEdit = () => {
    // The modal's internal save logic will handle the DB update.
    // Close edit mode and let the component logic automatically re-enter edit mode
    setIsEditModalOpen(false);
    setCurrentSetting(null);
    toast.success("Setting saved successfully!");
    runAutomaticConflictDetection(supabase);
    // NEW: Refresh coverage analysis when inline settings save
    if (onCoverageRefresh) {
      onCoverageRefresh();
    }
  };

  // NEW: Handle inline workflow saving with proper database integration
  const handleSaveWorkflow = async (data) => {
    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) throw new Error('User not authenticated');

      // Check if this is a real database ID or a temporary ID
      const isRealId = data.id && !data.id.toString().startsWith('add-') && !data.id.toString().startsWith('edit-') && !data.id.toString().startsWith('new-');

      if (isRealId) {
        // Update existing workflow
        const { error: settingsError } = await supabase
          .from('brain_settings')
          .update({
            name: data.name,
            description: data.description,
            triggers: data.triggers,
            category_id: data.category_id, // **FIX: Include category_id update**
          })
          .eq('id', data.id);

        if (settingsError) throw settingsError;

        // Get the workflow ID from brain_workflows table
        const { data: existingWorkflow, error: getWorkflowError } = await supabase
          .from('brain_workflows')
          .select('id')
          .eq('settings_id', data.id)
          .single();

        if (getWorkflowError) throw getWorkflowError;

        // Update workflow-specific data
        const { error: workflowError } = await supabase
          .from('brain_workflows')
          .update({
            name: data.name,
            description: data.description,
            trigger_phrases: data.workflow_config?.trigger_phrases || [],
            execution_timeout_seconds: data.workflow_config?.execution_timeout_seconds || 300,
            max_execution_depth: data.workflow_config?.max_execution_depth || 50,
            is_active: data.workflow_config?.is_active ?? true
          })
          .eq('settings_id', data.id);

        if (workflowError) throw workflowError;

        // Delete existing steps and their related data (CASCADE will handle references and conditions)
        const { error: deleteStepsError } = await supabase
          .from('brain_workflow_steps')
          .delete()
          .eq('workflow_id', existingWorkflow.id);

        if (deleteStepsError) throw deleteStepsError;

        // Create new workflow steps
        if (data.workflow_config?.steps && data.workflow_config.steps.length > 0) {
          // First, create all steps without parent relationships
          const stepsToCreate = data.workflow_config.steps.map(step => {
            const stepData = {
              workflow_id: existingWorkflow.id,
              step_name: step.name,
              step_type: step.type,
              step_order: step.order || 0,
              parent_step_id: null, // Set to null initially
              step_config: step.config || {},
              error_handling: { retry_count: 0, fallback_step_id: null },
              is_active: true
            };

            // Try to add node_position if it exists, but don't fail if column doesn't exist
            if (step.position) {
              stepData.node_position = step.position;
            }

            return stepData;
          });

          const { data: createdSteps, error: stepsError } = await supabase
            .from('brain_workflow_steps')
            .insert(stepsToCreate)
            .select();

          if (stepsError) throw stepsError;

          // Create a mapping from original step IDs to new database step IDs
          const stepIdMapping = {};
          data.workflow_config.steps.forEach((originalStep, index) => {
            stepIdMapping[originalStep.id] = createdSteps[index].id;
          });

          // Now update parent relationships using the real database IDs
          for (let i = 0; i < data.workflow_config.steps.length; i++) {
            const originalStep = data.workflow_config.steps[i];
            const createdStep = createdSteps[i];

            if (originalStep.parentId && stepIdMapping[originalStep.parentId]) {
              const { error: updateError } = await supabase
                .from('brain_workflow_steps')
                .update({ parent_step_id: stepIdMapping[originalStep.parentId] })
                .eq('id', createdStep.id);

              if (updateError) throw updateError;
            }
          }

          // Create step references for each step
          for (let i = 0; i < data.workflow_config.steps.length; i++) {
            const step = data.workflow_config.steps[i];
            const createdStep = createdSteps[i];

            // Save brain item references
            if (step.references && step.references.length > 0) {
              const referencesToCreate = step.references.map(ref => ({
                workflow_step_id: createdStep.id,
                reference_type: ref.reference_type,
                reference_id: ref.reference_id,
                execution_order: ref.execution_order || 1,
                input_mapping: {},
                output_mapping: {}
              }));

              const { error: referencesError } = await supabase
                .from('brain_workflow_step_references')
                .insert(referencesToCreate);

              if (referencesError) throw referencesError;
            }

            // Save conditions (update target_step_id to use real database IDs)
            if (step.conditions && step.conditions.length > 0) {
              const conditionsToCreate = step.conditions.map((condition, index) => {
                // **FIX: Ensure condition_name is never null or empty**
                let conditionName = condition.condition_name;
                if (!conditionName || conditionName.trim() === '') {
                  // Generate meaningful default name based on condition logic
                  if (condition.expected_value && condition.expected_value.trim() !== '') {
                    conditionName = `If ${condition.variable_path || 'input'} ${condition.condition_operator || 'equals'} "${condition.expected_value}"`;
                  } else {
                    conditionName = `Condition ${index + 1}`;
                  }
                }

                return {
                  workflow_step_id: createdStep.id,
                  condition_name: conditionName,
                  condition_type: condition.condition_type || 'equals',
                  variable_path: condition.variable_path,
                  expected_value: condition.expected_value,
                  condition_operator: condition.condition_operator,
                  target_step_id: condition.target_step_id && stepIdMapping[condition.target_step_id] 
                    ? stepIdMapping[condition.target_step_id] 
                    : null,
                  condition_order: condition.condition_order || (index + 1),
                  is_active: condition.is_active !== false
                };
              });

              const { error: conditionsError } = await supabase
                .from('brain_workflow_conditions')
                .insert(conditionsToCreate);

              if (conditionsError) throw conditionsError;
            }
          }
        }

        toast.success("Workflow updated successfully");

        // **OPTIMISTIC UI UPDATE**: Update parent categories state immediately
        if (onUpdateSetting) {
          onUpdateSetting(data.id, {
            name: data.name,
            description: data.description,
            triggers: data.triggers,
            category_id: data.category_id
          });
        }

        // Update local state for immediate UI feedback
        setSyncedSelectedItem(prev => ({
          ...prev,
          name: data.name,
          description: data.description,
          triggers: data.triggers,
          category_id: data.category_id,
          fullData: {
            ...prev.fullData,
            name: data.name,
            description: data.description,
            steps: data.workflow_config?.steps || []
          }
        }));

        // Update currentSetting to reflect changes in the modal
        setCurrentSetting(prev => ({
          ...prev,
          name: data.name,
          description: data.description,
          triggers: data.triggers,
          category_id: data.category_id
        }));

        // Run conflict detection
        runAutomaticConflictDetection(supabase);

        // Return success without closing modal (for inline editing)
        return Promise.resolve({ success: true, isUpdate: true, shouldClose: false });
      } else {
        // Create new workflow
        const { data: settingsData, error: settingsError } = await supabase
          .from('brain_settings')
          .insert({
            name: data.name,
            description: data.description,
            triggers: data.triggers,
            category_id: data.category_id,
            type: 'workflow'
          })
          .select('id')
          .single();

        if (settingsError) throw settingsError;

        const newSettingsId = settingsData.id;

        // Create the workflow record
        const { data: workflowData, error: workflowError } = await supabase
          .from('brain_workflows')
          .insert({
            settings_id: newSettingsId,
            name: data.name,
            description: data.description,
            trigger_phrases: data.workflow_config?.trigger_phrases || [],
            execution_timeout_seconds: data.workflow_config?.execution_timeout_seconds || 300,
            max_execution_depth: data.workflow_config?.max_execution_depth || 50,
            is_active: data.workflow_config?.is_active ?? true
          })
          .select('id')
          .single();

        if (workflowError) throw workflowError;

        // Create workflow steps (same logic as update case)
        if (data.workflow_config?.steps && data.workflow_config.steps.length > 0) {
          const stepsToCreate = data.workflow_config.steps.map(step => {
            const stepData = {
              workflow_id: workflowData.id,
              step_name: step.name,
              step_type: step.type,
              step_order: step.order || 0,
              parent_step_id: null,
              step_config: step.config || {},
              error_handling: { retry_count: 0, fallback_step_id: null },
              is_active: true
            };

            if (step.position) {
              stepData.node_position = step.position;
            }

            return stepData;
          });

          const { data: createdSteps, error: stepsError } = await supabase
            .from('brain_workflow_steps')
            .insert(stepsToCreate)
            .select();

          if (stepsError) throw stepsError;

          // Create step ID mapping and update parent relationships
          const stepIdMapping = {};
          data.workflow_config.steps.forEach((originalStep, index) => {
            stepIdMapping[originalStep.id] = createdSteps[index].id;
          });

          for (let i = 0; i < data.workflow_config.steps.length; i++) {
            const originalStep = data.workflow_config.steps[i];
            const createdStep = createdSteps[i];

            if (originalStep.parentId && stepIdMapping[originalStep.parentId]) {
              const { error: updateError } = await supabase
                .from('brain_workflow_steps')
                .update({ parent_step_id: stepIdMapping[originalStep.parentId] })
                .eq('id', createdStep.id);

              if (updateError) throw updateError;
            }
          }

          // Create step references and conditions (same logic as update case)
          for (let i = 0; i < data.workflow_config.steps.length; i++) {
            const step = data.workflow_config.steps[i];
            const createdStep = createdSteps[i];

            if (step.references && step.references.length > 0) {
              const referencesToCreate = step.references.map(ref => ({
                workflow_step_id: createdStep.id,
                reference_type: ref.reference_type,
                reference_id: ref.reference_id,
                execution_order: ref.execution_order || 1,
                input_mapping: {},
                output_mapping: {}
              }));

              const { error: referencesError } = await supabase
                .from('brain_workflow_step_references')
                .insert(referencesToCreate);

              if (referencesError) throw referencesError;
            }

            if (step.conditions && step.conditions.length > 0) {
              const conditionsToCreate = step.conditions.map((condition, index) => {
                let conditionName = condition.condition_name;
                if (!conditionName || conditionName.trim() === '') {
                  if (condition.expected_value && condition.expected_value.trim() !== '') {
                    conditionName = `If ${condition.variable_path || 'input'} ${condition.condition_operator || 'equals'} "${condition.expected_value}"`;
                  } else {
                    conditionName = `Condition ${index + 1}`;
                  }
                }

                return {
                  workflow_step_id: createdStep.id,
                  condition_name: conditionName,
                  condition_type: condition.condition_type || 'equals',
                  variable_path: condition.variable_path,
                  expected_value: condition.expected_value,
                  condition_operator: condition.condition_operator,
                  target_step_id: condition.target_step_id && stepIdMapping[condition.target_step_id] 
                    ? stepIdMapping[condition.target_step_id] 
                    : null,
                  condition_order: condition.condition_order || (index + 1),
                  is_active: condition.is_active !== false
                };
              });

              const { error: conditionsError } = await supabase
                .from('brain_workflow_conditions')
                .insert(conditionsToCreate);

              if (conditionsError) throw conditionsError;
            }
          }
        }

        toast.success("Workflow created successfully");

        // **OPTIMISTIC UI UPDATE**: Update parent categories state immediately
        if (onUpdateSetting) {
          onUpdateSetting(newSettingsId, {
            name: data.name,
            description: data.description,
            triggers: data.triggers,
            category_id: data.category_id
          });
        }

        // Update local state for immediate UI feedback
        setSyncedSelectedItem(prev => ({
          ...prev,
          id: newSettingsId,
          name: data.name,
          description: data.description,
          triggers: data.triggers,
          category_id: data.category_id,
          fullData: {
            ...prev.fullData,
            name: data.name,
            description: data.description,
            steps: data.workflow_config?.steps || []
          }
        }));

        // Update currentSetting to reflect changes in the modal
        setCurrentSetting(prev => ({
          ...prev,
          id: newSettingsId,
          name: data.name,
          description: data.description,
          triggers: data.triggers,
          category_id: data.category_id
        }));

        // Run conflict detection
        runAutomaticConflictDetection(supabase);

        // Create formatted setting data for local UI update
        const newSettingData = {
          id: newSettingsId,
          name: data.name,
          description: data.description,
          category_id: data.category_id,
          triggers: data.triggers,
          type: 'workflow',
          original_type: 'workflow',
          fullData: {
            settings_id: newSettingsId,
            name: data.name,
            description: data.description,
            steps: data.workflow_config?.steps || [],
            trigger_phrases: data.workflow_config?.trigger_phrases || [],
            execution_timeout_seconds: data.workflow_config?.execution_timeout_seconds || 300,
            max_execution_depth: data.workflow_config?.max_execution_depth || 50,
            is_active: data.workflow_config?.is_active ?? true,
            category_id: data.category_id
          },
          locationVariants: [] // Start with empty variants, will be populated when fetched
        };

        // Return success without closing modal (for inline editing)
        return Promise.resolve({ 
          success: true, 
          id: newSettingsId, // WorkflowModal expects 'id' field
          settingsId: newSettingsId, // For coverage analysis
          isCreate: true, // Mark as create operation
          shouldClose: false,
          data: newSettingData // For local UI updates
        });
      }
    } catch (err) {
      console.error('Error saving workflow:', err);
      toast.error("Failed to save workflow");
      return Promise.reject(err);
    }
  };

  // Helper functions from Brain.js to load setting data
  const loadSimpleAction = async (setting) => {
    // Protect against temporary IDs from new items
    if (setting.id && setting.id.toString().startsWith('new-')) {
      return {
        id: null,
        settings_id: setting.id,
        name: setting.name || '',
        description: setting.description || '',
        triggers: setting.triggers || [],
        type: 'sms', // Default action type
        category_id: setting.category_id,
        is_active: true,
        all_locations: true,
        selected_locations: [],
      };
    }

    const { data: saRows, error: saErr } = await supabase
      .from('brain_simple_actions')
      .select('*')
      .eq('settings_id', setting.id);
    if (saErr) throw saErr;

    const firstRow = saRows && saRows.length ? saRows[0] : null;
    const actionType = firstRow?.action_type || 'sms';

    const action = {
      id: firstRow?.id || null,
      settings_id: setting.id,
      name: setting.name,
      description: setting.description || '',
      triggers: setting.triggers || [],
      type: actionType,
      category_id: setting.category_id,
      is_active: true,
      all_locations: true,
      selected_locations: [],
    };
    return action;
  };

  // Calculate real capabilities from brain data for an integration
  const calculateIntegrationCapabilities = async (providerId) => {
    if (!providerId || !categories.length) {
      return {
        simpleAnswers: { count: 0, items: [] },
        complexAnswers: { count: 0, items: [] },
        simpleActions: { count: 0, items: [] },
        workflows: { count: 0, items: [] }
      };
    }

    try {
      const { getUserAccountId } = await import('../utils/accountUtils');
      const { accountId } = await getUserAccountId();
      if (!accountId) return { simpleAnswers: { count: 0, items: [] }, complexAnswers: { count: 0, items: [] }, simpleActions: { count: 0, items: [] }, workflows: { count: 0, items: [] } };

      // Find categories linked to this provider for this account
      const { data: linkedCategories, error: catError } = await supabase
        .from('brain_categories')
        .select('id, name')
        .eq('account_id', accountId)
        .eq('api_provider_id', providerId);

      if (catError) throw catError;

      if (!linkedCategories?.length) {
        return {
          simpleAnswers: { count: 0, items: [] },
          complexAnswers: { count: 0, items: [] },
          simpleActions: { count: 0, items: [] },
          workflows: { count: 0, items: [] }
        };
      }

      const categoryIds = linkedCategories.map(cat => cat.id);

      // Get all brain_settings for these categories
      const { data: settings, error: settingsError } = await supabase
        .from('brain_settings')
        .select('id, name, description, type, category_id, triggers')
        .in('category_id', categoryIds)
        .eq('status', 'live');

      if (settingsError) throw settingsError;

      // Group settings by type and format for display
      const simpleAnswers = settings.filter(s => s.type === 'simple_answer');
      const complexAnswers = settings.filter(s => s.type === 'complex_answer');
      const simpleActions = settings.filter(s => s.type === 'simple_action');
      const workflows = settings.filter(s => s.type === 'workflow');

      // For complex answers, fetch their questions
      const complexAnswerQuestions = {};
      if (complexAnswers.length > 0) {
        const complexAnswerSettingsIds = complexAnswers.map(ca => ca.id);

        // First get the brain_complex_answers records
        const { data: complexAnswerRecords, error: complexAnswerError } = await supabase
          .from('brain_complex_answers')
          .select('id, settings_id, answer_type')
          .in('settings_id', complexAnswerSettingsIds);

        if (!complexAnswerError && complexAnswerRecords?.length > 0) {
          const complexAnswerIds = complexAnswerRecords.map(ca => ca.id);

          // Then get the questions using complex_answer_id (fix column names)
          const { data: questionsData, error: questionsError } = await supabase
            .from('brain_complex_answers_questions')
            .select('complex_answer_id, question, "order"')
            .in('complex_answer_id', complexAnswerIds)
            .order('"order"');

          // Get API details for API-type complex answers
          const apiComplexAnswers = complexAnswerRecords.filter(ca => ca.answer_type === 'api');
          const apiDetailsMap = {};
          if (apiComplexAnswers.length > 0) {
            const apiComplexAnswerIds = apiComplexAnswers.map(ca => ca.id);
            const { data: apiData, error: apiError } = await supabase
              .from('brain_complex_answers_api')
              .select('complex_answer_id, api_endpoint, api_method')
              .in('complex_answer_id', apiComplexAnswerIds);

            if (!apiError && apiData) {
              apiData.forEach(api => {
                apiDetailsMap[api.complex_answer_id] = {
                  endpoint: api.api_endpoint,
                  method: api.api_method
                };
              });
            }
          }

          if (!questionsError && questionsData) {
            // Create mapping from complex_answer_id to settings_id
            const complexAnswerToSettingsMap = {};
            const complexAnswerTypeMap = {};
            complexAnswerRecords.forEach(ca => {
              complexAnswerToSettingsMap[ca.id] = ca.settings_id;
              complexAnswerTypeMap[ca.id] = ca.answer_type;
            });

            questionsData.forEach(q => {
              const settingsId = complexAnswerToSettingsMap[q.complex_answer_id];
              if (settingsId) {
                if (!complexAnswerQuestions[settingsId]) {
                  complexAnswerQuestions[settingsId] = {
                    questions: [],
                    answerType: complexAnswerTypeMap[q.complex_answer_id],
                    apiDetails: apiDetailsMap[q.complex_answer_id] || null
                  };
                }
                complexAnswerQuestions[settingsId].questions.push(q.question);
              }
            });
          }
        }
      }

      // Create lookup for category names
      const categoryLookup = {};
      linkedCategories.forEach(cat => {
        categoryLookup[cat.id] = cat.name;
      });

      return {
        simpleAnswers: {
          count: simpleAnswers.length,
          items: simpleAnswers.map(s => ({
            title: s.name,
            description: s.description || `Simple answer from ${categoryLookup[s.category_id]}`,
            triggers: s.triggers || []
          }))
        },
        complexAnswers: {
          count: complexAnswers.length,
          items: complexAnswers.map(s => ({
            title: s.name,
            description: s.description || `Complex answer from ${categoryLookup[s.category_id]}`,
            triggers: s.triggers || [],
            questions: complexAnswerQuestions[s.id]?.questions || [],
            answerType: complexAnswerQuestions[s.id]?.answerType || 'knowledge',
            apiDetails: complexAnswerQuestions[s.id]?.apiDetails || null
          }))
        },
        simpleActions: {
          count: simpleActions.length,
          items: simpleActions.map(s => ({
            title: s.name,
            description: s.description || `Action from ${categoryLookup[s.category_id]}`,
            triggers: s.triggers || []
          }))
        },
        workflows: {
          count: workflows.length,
          items: workflows.map(s => ({
            title: s.name,
            description: s.description || `Workflow from ${categoryLookup[s.category_id]}`,
            triggers: s.triggers || []
          }))
        }
      };
    } catch (err) {
      console.error('Error calculating integration capabilities:', err);
      return {
        simpleAnswers: { count: 0, items: [] },
        complexAnswers: { count: 0, items: [] },
        simpleActions: { count: 0, items: [] },
        workflows: { count: 0, items: [] }
      };
    }
  };

  const loadSimpleAnswer = async (setting) => {
    // Protect against temporary IDs from new items
    if (setting.id && setting.id.toString().startsWith('new-')) {
      return {
        id: null,
        settings_id: setting.id,
        name: setting.name || '',
        question: setting.name || '',
        answer: '',
        category_id: setting.category_id,
      };
    }

    // 1. Get ALL simple_answer rows for this setting
    const { data: answerRows, error: answerErr } = await supabase
      .from('brain_simple_answers')
      .select('*')
      .eq('settings_id', setting.id);
    if (answerErr) throw answerErr;

    const firstRow = answerRows && answerRows.length ? answerRows[0] : null;

    // Base answer object
    const answer = {
      id: firstRow?.id || null,
      settings_id: setting.id,
      name: setting.name,
      question: firstRow?.title || setting.name || '',
      answer: firstRow?.content || '',
      category_id: setting.category_id,
    };

    // 2. Fetch location groupings
    const { data: groupRows } = await supabase
      .from('brain_groupings')
      .select('*')
      .eq('settings_id', setting.id)
      .order('created_at');
    const locationGroupings = groupRows || [];

    // 3. Build assignment map
    let locationAssignments = {};
    if (locationGroupings.length) {
      const groupIds = locationGroupings.map((g) => g.id);
      const { data: locRows } = await supabase
        .from('brain_grouping_locations')
        .select('grouping_id, location_id')
        .in('grouping_id', groupIds);
      (locRows || []).forEach((row) => {
        if (!locationAssignments[row.grouping_id]) locationAssignments[row.grouping_id] = [];
        locationAssignments[row.grouping_id].push(row.location_id);
      });
    }

    // 4. Build mapping of grouping -> form fields (question/answer)
    const formDataByGrouping = {};
    (answerRows || []).forEach(row => {
      formDataByGrouping[row.grouping_id] = {
        question: row.title || '',
        answer: row.content || ''
      };
      // Update the main answer object with the first grouping's data
      if (row.grouping_id === firstRow?.grouping_id) {
        answer.question = row.title || '';
        answer.answer = row.content || '';
      }
    });

    // Attach grouping/variant info
    answer.locationGroupings = locationGroupings;
    answer.locationAssignments = locationAssignments;
    answer.showLocationVariants = locationGroupings.length > 1;
    answer.formDataByGrouping = formDataByGrouping;
    answer.mainGroupingId = firstRow?.grouping_id || null;

    return answer;
  };

  // If no item is selected OR multilingual is selected, show settings page
  if (!syncedSelectedItem || syncedSelectedItem.type === 'multilingual') {
    return (
      <div 
        className="flex-1 bg-gray-50 h-full overflow-hidden brain-main-content"
        onWheel={(e) => e.stopPropagation()}
      >
        <div className="h-full overflow-y-auto overscroll-none">
          <div className={cn("min-h-full flex flex-col", isMobile ? "pb-6 px-6 pt-6" : "py-8 px-8")}>
            {/* Mobile Header for Settings */}
            {isMobile && onMobileSidebarToggle && (
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-inner">
                    <Gear className="w-5 h-5 text-gray-600" />
                  </div>
                  <h1 className="text-xl font-bold text-gray-900">Settings</h1>
                </div>
                <button
                  onClick={onMobileSidebarToggle}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Open sidebar"
                >
                  <FolderOpen className="w-5 h-5 text-gray-600" />
                </button>
              </div>
            )}
            <MultilingualSettings onUpdateSettings={onUpdateMultilingualSettings} />
          </div>
        </div>
      </div>
    );
  }

  // Show the First Message editor
  if (syncedSelectedItem.type === 'firstMessage') {
    return (
        <div 
            className="flex-1 bg-white h-full overflow-hidden brain-main-content"
            onWheel={(e) => e.stopPropagation()}
        >
            <FirstMessageEditor 
              isMobile={isMobile} 
              isSidePanelOpen={isSidePanelOpen}
              onMobileSidebarToggle={onMobileSidebarToggle} 
            />
        </div>
    );
  }

  // If a setting or variant is selected, render the appropriate "modal" component inline
  if (isEditModalOpen && (syncedSelectedItem.type === 'setting' || syncedSelectedItem.type === 'variant') && currentSetting) {
    const componentType = syncedSelectedItem.type === 'variant' ? 
      syncedSelectedItem.parentSetting?.original_type || syncedSelectedItem.parentSetting?.type :
      syncedSelectedItem.original_type;

    return (
      <div 
        className="flex-1 bg-white h-full flex flex-col overflow-auto"
        onWheel={(e) => e.stopPropagation()}
      >
        {componentType === 'simple_action' && syncedSelectedItem.type === 'variant' && (
          <SimpleActionContentModal
            isOpen={true}
            onClose={handleCloseEdit}
            variant={syncedSelectedItem}
            isInline={true}
          />
        )}

        {componentType === 'simple_action' && syncedSelectedItem.type === 'setting' && (
          <CallActionSettingsModal
            isOpen={true}
            onClose={handleCloseEdit}
            onSave={handleSaveEdit}
            onUpdateSetting={onUpdateSetting}
            action={currentSetting}
            isNew={syncedSelectedItem.isNew || false}
            isInline={true}
            hideActionTab={syncedSelectedItem.locationVariants && syncedSelectedItem.locationVariants.length > 1}
            onCreateCategory={onCreateCategory}
            newlyCreatedCategory={newlyCreatedCategory}
            onClearNewlyCreatedCategory={onClearNewlyCreatedCategory}
            onOpenActivationConditions={onOpenActivationConditions}
            setting={syncedSelectedItem}
          />
        )}

        {componentType === 'complex_answer' && syncedSelectedItem.type === 'variant' && (
          <ComplexAnswerContentModal
            isOpen={true}
            onClose={handleCloseEdit}
            variant={syncedSelectedItem}
            isInline={true}
          />
        )}

        {componentType === 'workflow' && (
          <WorkflowModal
            isOpen={true}
            onClose={handleCloseEdit}
            onSave={handleSaveWorkflow}
            onUpdateSetting={onUpdateSetting}
            workflow={currentSetting}
            isNew={syncedSelectedItem.isNew || false}
            isInline={true}
            categories={categories}
            onCreateCategory={onCreateCategory}
            newlyCreatedCategory={newlyCreatedCategory}
            onClearNewlyCreatedCategory={onClearNewlyCreatedCategory}
            onOpenActivationConditions={onOpenActivationConditions}
            setting={syncedSelectedItem}
          />
        )}

        {componentType === 'complex_answer' && syncedSelectedItem.type === 'setting' && (
          <ComplexAnswerSettingsModal
            isOpen={true}
            onClose={handleCloseEdit}
            onSave={handleSaveEdit}
            onUpdateSetting={onUpdateSetting}
            answer={currentSetting}
            isNew={syncedSelectedItem.isNew || false}
            isInline={true}
            hideContentTab={syncedSelectedItem.locationVariants && syncedSelectedItem.locationVariants.length > 1}
            onCreateCategory={onCreateCategory}
            newlyCreatedCategory={newlyCreatedCategory}
            onClearNewlyCreatedCategory={onClearNewlyCreatedCategory}
            isSidePanelOpen={isSidePanelOpen}
            onOpenActivationConditions={onOpenActivationConditions}
            setting={syncedSelectedItem}
          />
        )}

        {componentType === 'simple_answer' && (
          <SimpleAnswerModal
            isOpen={true}
            onClose={handleCloseEdit}
            onSave={handleSaveEdit}
            onUpdateSetting={onUpdateSetting}
            answer={currentSetting}
            isNew={syncedSelectedItem.isNew || false}
            isInline={true}
            onCreateCategory={onCreateCategory}
            newlyCreatedCategory={newlyCreatedCategory}
            onClearNewlyCreatedCategory={onClearNewlyCreatedCategory}
            onOpenActivationConditions={onOpenActivationConditions}
            setting={syncedSelectedItem}
          />
        )}
      </div>
    );
  }

  // For settings and variants, we should only show edit mode, never the details view
  // Show loading state while edit mode initializes (edit mode is triggered by useEffect above)
  if (syncedSelectedItem.type === 'setting' || syncedSelectedItem.type === 'variant') {
    if (!isEditModalOpen || !currentSetting) {
      return (
        <div className="flex-1 bg-white h-full overflow-hidden flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black mx-auto mb-2"></div>
            <p className="text-sm text-gray-500">Loading...</p>
          </div>
        </div>
      );
    }
  }

  // Handle Clarification Questions
  if (syncedSelectedItem.type === 'clarification_question') {
    return (
      <ClarificationQuestionEditor
        question={syncedSelectedItem}
        onSave={onUpdateClarificationQuestion}
        onDelete={onDeleteClarificationQuestion}
        isMobile={isMobile}
      />
    );
  }

  // Handle integration display - clean and elegant like BrainSidebar
  if (syncedSelectedItem.type === 'integration') {
    // This now assumes all integrations are V2
        return (
      <IntegrationV2Content
        selectedItem={syncedSelectedItem}
        onConnect={onConnectIntegrationV2}
        onDisconnect={onDisconnectIntegrationV2}
        // ... pass other V2 handlers
      />
    );
  }

  // Default view mode for categories only
  const IconComponent = getIcon(syncedSelectedItem);
  return (
    <div 
      className="flex-1 bg-white h-full overflow-hidden"
      onWheel={(e) => e.stopPropagation()}
    >
      <div className="h-full overflow-hidden">
        <div className={cn("h-full flex flex-col justify-center", isMobile ? "pb-6 px-6" : "py-8 px-8")}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className={cn("", isMobile ? "pt-0 pb-4 px-4" : "pt-4 pb-8 px-8")}
          >
            <div className={cn("flex items-start justify-between", isMobile ? "mb-4" : "mb-6")}>
              <div className="flex items-start space-x-4">
                <div className="p-3 bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg shadow-inner">
                  <IconComponent className="w-6 h-6 text-gray-600" />
                </div>
                <div>
                  <h1 className={cn("font-bold text-gray-900 mb-1", isMobile ? "text-xl" : "text-2xl")}>
                    {syncedSelectedItem.name}
                  </h1>
                  <div className="flex items-center space-x-2">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {getTypeLabel(syncedSelectedItem)}
                    </span>
                    {/* Activation Conditions Button - Only for settings */}
                    {syncedSelectedItem.type === 'setting' && onOpenActivationConditions && (
                      <button
                        onClick={() => onOpenActivationConditions(syncedSelectedItem)}
                        className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                        title="Manage when this setting should be active"
                      >
                        <Clock className="w-3 h-3 mr-1" />
                        Conditions
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* Mobile Sidebar Toggle */}
              {isMobile && onMobileSidebarToggle && (
                <button
                  onClick={onMobileSidebarToggle}
                  className="p-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
                  aria-label="Open sidebar"
                >
                  <FolderOpen className="w-5 h-5 text-gray-600" />
                </button>
              )}
            </div>

            <div className="space-y-6">
              {syncedSelectedItem.description && (
                <div>
                  <h3 className={cn("font-medium text-gray-900 mb-3", isMobile ? "text-base" : "text-lg")}>Description</h3>
                  <p className={cn("text-gray-700 leading-relaxed", isMobile ? "text-sm" : "text-base")}>{syncedSelectedItem.description}</p>
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </div>


    </div>
  );
};

export default BrainMainContent; 